import React, { useEffect } from "react";
import { Badge, Col } from "react-bootstrap";
import { FormikProps } from "formik";
import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../../assets/layouts";
import {
  SortDirection,
  SortModel,
} from "../../../../assets/utils/lib/searchDto";
import Form from "../../../../assets/vfx/form/form";
import { ResourceOrganizationService } from "./organizationService";
import { Constants } from "../../../../assets/utils/lib/constants";
import { GRID_ACTION_TYPES, SetState } from "../../../../assets/types";

import { SearchContainer } from "../../../../assets/vfx/search/searchContainer";
import { VfxLib } from "../../../../assets/vfx";
import Control from "../../../../assets/vfx/controls/control";
import { ViewProps } from "./props";
import { useCommunity } from "../../../../context/communityContext";
import { OrganizationGridDto } from "../../../../services/dto/communityResource/organizationGridDto";
import { FormatHelper } from "../../../../assets/utils/helpers";

interface ResourceOrganizationPageProps {
  controlStyles?: IControlStyles;
  lookups?: ViewProps.OrganizationLookupProps | undefined;
  communityId?: string;
  hideTitle?: boolean;
  onCustomEvent?: (actionType: string, dataId: string) => void;
  setCommunity: SetState<string | undefined>;
}

export interface ResourceOrganizationSearchProps {
  communityId: string;
}

const defaultSortModel: SortModel = {
  field: "name",
  direction: SortDirection.Ascending,
};

export const isNewRequest = (status: string | undefined): boolean => {
  return ["new-request", "New"].includes(status ?? "");
};

const renderCategoryPills = (
  categoryIds: string[] | undefined,
  lookups: ViewProps.OrganizationLookupProps | undefined
): React.JSX.Element | null => {
  const categories = categoryIds
    ?.map((category) => lookups?.category.find((x) => x.value === category)?.display)
    .filter((category): category is string => Boolean(category));

  if (!categories || categories.length === 0) return null;

  return (
    <div className="d-flex flex-wrap gap-2">
      {categories.map((category, index) => (
        <Badge
          key={`${category}-${index}`}
          pill
          bg="transparent"
          text="dark"
          className="border border-dark px-3 py-2"
        >
          {category}
        </Badge>
      ))}
    </div>
  );
};

export interface SearchOrganizationDto {
  communityId?: string | undefined;
  categoryId?: string | undefined;
  name?: string | undefined;
  sortBy: SortModel;
  pageSize?: number | undefined;
  pageNumber?: number | undefined;
}

type OrganizationGridRow = OrganizationGridDto & {
  addressBlock?: string[] | undefined;
};

export const ResourceOrganizationPage = (
  props: ResourceOrganizationPageProps
) => {
  const communityContext = useCommunity();
  let communityId = props.communityId;
  if (!communityId) communityId = communityContext.communityId ?? undefined;

  const defaultSearchQuery: SearchOrganizationDto = {
    pageSize: 10,
    pageNumber: 1,
    sortBy: defaultSortModel,
    name: Constants.emptyString,
    ...(communityId && { communityId: communityId }),
  };

  const [searchQuery, setSearchQuery] =
    React.useState<SearchOrganizationDto>(defaultSearchQuery);

  useEffect(() => {
    if (communityId && communityId !== Constants.emptyGuid) {
      setSearchQuery((q) => ({ ...q, communityId }));
    }

    if (communityId) {
      props.setCommunity(communityId);
    }
  }, [communityId]);

  const styles: IControlStyles = props.controlStyles ?? useControlStyles();

  const resourceOrganizationGridQuery = ResourceOrganizationService.gridQuery(searchQuery);
  const isGridLoading = resourceOrganizationGridQuery.isLoading || resourceOrganizationGridQuery.isFetching;
  const categoryLookupGridKey = React.useMemo(
    () =>
      props.lookups?.category
        ?.map((category) => `${category.value}:${category.display}`)
        .join("|") ?? "category-lookups-loading",
    [props.lookups?.category]
  );

  const getUpdatedSearchQuery = (
    values: SearchOrganizationDto
  ): SearchOrganizationDto => ({
      ...defaultSearchQuery,
      pageSize: searchQuery.pageSize,
      pageNumber: searchQuery.pageNumber ?? 1,
      name: values.name,
      ...(values.categoryId && { categoryId: values.categoryId }),
  });

  const applySearch = (values: SearchOrganizationDto): void => {
    setSearchQuery(getUpdatedSearchQuery(values));

    if (communityId) {
      props.setCommunity(communityId);
    }
  };

  const handleSearch = (values: SearchOrganizationDto): void => {
    applySearch(values);
  };

  const handleCategoryChange = (
    formikProps: FormikProps<object>,
    value: string
  ): void => {
    applySearch({
      ...(formikProps.values as SearchOrganizationDto),
      categoryId: value,
    });
  };

  const handleClear = (): void => {
    setSearchQuery((prev) => ({
      ...defaultSearchQuery,
      sortBy: prev.sortBy,
      pageSize: prev.pageSize ?? 10,
    }));
  };

  const handleNew = (): void | undefined =>
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  const searchContainer: React.JSX.Element = (
    <SearchContainer
      initialValues={{
        categoryId: Constants.emptyString,
        name: Constants.emptyString,
      }}
      enableReinitialize
      onSearch={handleSearch}
      onClear={handleClear}
    >
      {(formikProps: FormikProps<object>) => (
        <>
          <Col lg={3}>
            <Form.Dropdown
              name="categoryId"
              label="Category"
              blankOption={{
                key: Constants.emptyGuid,
                display: "Select Category",
                value: Constants.emptyString,
              }}
              options={props.lookups?.category}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleCategoryChange(formikProps, event.target.value)
              }
            />
          </Col>
          <Col lg={3} className="ms-2">
            <Form.Text name="name" label="Name" />
          </Col>
        </>
      )}
    </SearchContainer>
  );

  const resourceOrganizationPageContent: React.JSX.Element = (
    <>
        <Control.DataGrid
          key={categoryLookupGridKey}
          dataId="organizationId"
          height="55vh"
          cols={[
            {
              data: "organizationId",
              title: "ID",
              visible: false,
              columnType: "number",
              alignment: "right",
            },
            {
              data: "name",
              title: "Name",
              visible: true,
              columnType: "custom",
              alignment: "left",
              sortable: true,
              width: "35%",
              customCell: (id: string, rowData: OrganizationGridRow) => {
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <span className="vfx-datagrid-resource-name">
                      {rowData.name ?? "Untitled"}
                    </span>
                    {renderCategoryPills(rowData.categoryIds, props.lookups)}
                  </div>
                );
              },
            },
            {
              data: "addressBlock",
              title: "Address",
              visible: true,
              columnType: "custom",
              alignment: "left",
              sortable: false,
              width: "25%",
              customCell: (id: string, rowData: OrganizationGridRow) => {
                const cityStateZip = [
                  [rowData.city, rowData.state].filter(Boolean).join(", "),
                  rowData.zip,
                ]
                  .filter(Boolean)
                  .join(" ");
                const addressLines =
                  rowData.addressBlock && rowData.addressBlock.length > 0
                    ? rowData.addressBlock
                    : [
                        rowData.addressLine1,
                        rowData.addressLine2,
                        cityStateZip,
                      ];

                const filteredAddressLines = addressLines.filter(
                  (line): line is string => Boolean(line)
                );

                if (filteredAddressLines.length === 0) return null;

                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {filteredAddressLines.map((line, index) => (
                      <span key={`${line}-${index}`}>{line}</span>
                    ))}
                  </div>
                );
              },
            },
            {
              data: "contactEmail",
              title: "Contact",
              visible: true,
              columnType: "custom",
              alignment: "left",
              sortable: false,
              width: "25%",
              customCell: (id: string, rowData: OrganizationGridRow) => {
                const phone = rowData.contactPhone
                  ? FormatHelper.formatPhone(rowData.contactPhone)
                  : undefined;
                const contactLines = [rowData.contactEmail, phone].filter(
                  (line): line is string => Boolean(line)
                );

                if (contactLines.length === 0) return null;

                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {contactLines.map((line, index) => (
                      <span key={`${line}-${index}`}>{line}</span>
                    ))}
                  </div>
                );
              },
            },
            {
              data: "activationDate",
              title: "Activation Date",
              visible: true,
              columnType: "date",
              alignment: "left",
              width: "15%",
            },
          ]}
          actionCol={{
            data: "id",
            title: "Action",
            visible: true,
            columnType: "action",
            alignment: "right",
            width: "100px",
            controlStyles: styles,
            actionButtons: [
                VfxLib.datagridActionButtons({ datagridStyles: styles }).edit,
                VfxLib.datagridActionButtons({ datagridStyles: styles }).delete,
              ],
          }}
          onCustomEvent={props.onCustomEvent}
          data={resourceOrganizationGridQuery?.data?.result ?? []}
          isActionButtonClick={true}
          totalRecords={
            resourceOrganizationGridQuery?.data?.totalSize ??
            resourceOrganizationGridQuery?.data?.result?.length ??
            0
          }
          query={searchQuery}
          loading={isGridLoading}
          onQueryChange={(patch) =>
            setSearchQuery((query) => ({ ...query, ...patch }))
          }
          uiOptions={{
            title: props.hideTitle ? undefined : "Community Resource",
            filterContent: searchContainer,
            primaryAction: props.hideTitle
              ? undefined
              : {
                  text: "Add Community Resource",
                  icon: "bi bi-plus-lg",
                  onClick: handleNew,
                },
          }}
        />
    </>
  );

  return props.hideTitle ? (
    resourceOrganizationPageContent
  ) : (
          <PageContent hideHeader>
      {resourceOrganizationPageContent}
    </PageContent>
  );
};
